// Transfer an order between the distribution location and its community hubs on a given day.
// Also updates payment history.
import React, { useState, useEffect, useContext } from "react";
import firebase from "../../../components/Firebase.js";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CopyOrdersBetweenLocations from "../Functions/CopyOrdersBetweenLocations.js";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import IconButton from "@material-ui/core/IconButton";
import EastIcon from "@mui/icons-material/East";
import SimpleSelect from "../../../components/userInputs/DropDownMenu.js";
import LoadingContent from "../../../components/LoadingContent.jsx";
import { CommunityOrderChunksContext } from "../../../pages/MyAccount.js";
import CheckOrderSync from "../../../functions/CheckOrderSync.js";

export default function CopyOrdersBetweenLocationsDialog({
  distributionLocation,
  orderDate,
  userOrderIdentifier,
  distributionDate,
  order,
  userInfo,
  orderLog,
  orderIndex,
  updateOrderLog,
  updatePaymentHistory,
}) {
  const [open, setOpen] = useState(false);
  const [pickupLocationToUpdate, setPickupLocationToUpdate] = useState("");

  // Each key is a location name; value is [legacyImportedFlag, selectedDate, communityHubDoc?].
  const [listOfLocations, setListOfLocations] = useState("loading");

  // Distribution date for this order at the current pickup (main site or hub).
  const [distributionDateOriginal, setDistributionDateOriginal] =
    useState(distributionDate);

  // This is the original pickupLocation.  It might change if the user is using a communty hub
  // and it's not the distribution location.
  const [pickupLocation, setPickupLocation] = useState(distributionLocation);

  // communityOrderChunks are in {orderdate: [communityOrders, overFlowCommunityOrders1, etc.]}
  const { communityOrderChunks = [], setCommunityOrderChunks = () => {} } =
    useContext(CommunityOrderChunksContext) || {};

  // If the order uses a community hub, pickup is the hub name rather than the DC key.
  useEffect(() => {
    if (
      !(order.communityOrders[userOrderIdentifier].communityPickup.length === 0)
    ) {
      setPickupLocation(
        order.communityOrders[userOrderIdentifier].communityPickup.locationName,
      );
    }
  }, []);

  // This useEffect loads all the community hubs into the list so that they can be selected from by the user
  // and updates the distribution original date in case it's a community hub.
  useEffect(() => {
    // If the dialog is open.  We don't want to do this if it's closed because then we will have
    // unnecessary calls to the database.
    if (open) {
      // The database in firebase.
      const database = firebase.firestore();
      // Initialize the list of locations.  We know that the distribution location will be at least one.
      // Set the location to not imported and the order selected date.
      const listOfLocationsTemp = {
        [userInfo.organisationName]: [false, order.selectedDate],
      };

      // Hubs store distributionLocationName from DistributionLocations.displayName, which may
      // differ from organisationName (the Vancouver doc key).
      const hubPartnerNames = [
        ...new Set(
          [userInfo.organisationName, userInfo.organisationDisplayName].filter(
            (n) => n != null && String(n).trim() !== "",
          ),
        ),
      ];

      const hubNameChunks = [];
      for (let i = 0; i < hubPartnerNames.length; i += 10) {
        hubNameChunks.push(hubPartnerNames.slice(i, i + 10));
      }

      Promise.all(
        hubNameChunks.map((names) =>
          database
            .collection("CommunityLocations")
            .where("distributionLocationName", "in", names)
            .orderBy("locationName")
            .get(),
        ),
      )
        .then((snapshots) => {
          const seenDocIds = new Set();
          snapshots.forEach((collection) => {
            collection.docs.forEach((doc) => {
              if (!doc.exists || seenDocIds.has(doc.id)) {
                return;
              }
              seenDocIds.add(doc.id);
              const communityLocationInfo = doc.data();
              listOfLocationsTemp[communityLocationInfo.locationName] = [
                false,
                order.selectedDate,
                communityLocationInfo,
              ];
            });
          });

          setDistributionDateOriginal(listOfLocationsTemp[pickupLocation][1]);

          // Remove the current location from the list as we don't want to transfer the
          // order to the same location it is already at.
          delete listOfLocationsTemp[pickupLocation];

          // Set the list to contain all the new possible locations.
          setListOfLocations({ ...listOfLocationsTemp });
        })
        .catch(function (error) {
          console.log("Error getting document:", error);
        });
    }
  }, [open]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Save the changes.
  const handleSave = async (e) => {
    e.preventDefault();
    // checks if the user has a stale webpage
    const { refreshNeeded } = await CheckOrderSync(orderLog[orderIndex]);
    // If it is stale prevents any changes and tells the user to refresh.
    if (refreshNeeded) {
      alert("You're webpage is out of date.  Please refresh.");
      return; // ⛔ Stop — prevents overwrite
    }

    // Updates the database in the DistributionLocations, Users order and the payment history.
    CopyOrdersBetweenLocations(
      orderDate,
      userOrderIdentifier,
      pickupLocationToUpdate,
      listOfLocations,
      pickupLocation,
      distributionLocation,
      distributionDateOriginal,
      orderLog,
      orderIndex,
      updateOrderLog,
      updatePaymentHistory,
      communityOrderChunks,
      setCommunityOrderChunks,
    );
    setOpen(false);
  };

  // Checks if the user can click save.  The user must first select a location to save to.
  let canSave = false;

  // Check to make sure that pickupLocationToUpdate is a value that isn't empty or the current location.
  if (
    !!pickupLocationToUpdate &&
    pickupLocationToUpdate !== "" &&
    pickupLocationToUpdate !== pickupLocation
  ) {
    // Set this to true so the user can save
    canSave = true;
  }

  return (
    <div>
      <IconButton onClick={handleClickOpen}>
        <SwapHorizIcon />
      </IconButton>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          id="form-dialog-title"
          style={{ padding: "16px 24px 0px" }}
        >
          Swap Order Between Locations
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSave}>
            <span>
              {pickupLocation}
              <EastIcon />
              {listOfLocations === "loading" ? (
                <LoadingContent inline={true} />
              ) : (
                <SimpleSelect
                  margin="dense"
                  id="selectOrderStatsLocation"
                  options={Object.keys(listOfLocations)}
                  handleChange={(e) =>
                    setPickupLocationToUpdate(e.target.value)
                  }
                  selected={pickupLocationToUpdate}
                />
              )}
            </span>
            <DialogActions>
              <Button onClick={() => handleClose()} color="primary">
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={!canSave}>
                Save
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
